import type { Permission } from '@/auth/permissions';

/**
 * The nine navigable routes, in the order figure 7.2 draws them.
 *
 * Each carries the permissions that make it worth showing. The nav renders
 * only permitted routes — a role without `reports.read` never sees Reports,
 * because a nav item that leads to a 403 is a support call waiting to happen.
 */
export interface NavItem {
  readonly to: string;
  readonly label: string;
  /** Shown when the user holds any one of these. */
  readonly anyOf: readonly Permission[];
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/map', label: 'Live map', anyOf: ['bays.read'] },
  { to: '/dashboard', label: 'Dashboard', anyOf: ['bays.read'] },
  { to: '/analytics', label: 'Analytics', anyOf: ['bays.read'] },
  { to: '/zones', label: 'Zones', anyOf: ['zones.read'] },
  { to: '/alerts', label: 'Alerts', anyOf: ['alerts.read'] },
  { to: '/reports', label: 'Reports', anyOf: ['reports.read'] },
  { to: '/health', label: 'Health', anyOf: ['health.read'] },
  {
    to: '/admin/data',
    label: 'Master data',
    anyOf: ['bays.write', 'zones.write', 'devices.map', 'calibration.read', 'signs.write'],
  },
  {
    to: '/admin/access',
    label: 'Access',
    anyOf: ['users.read', 'roles.manage', 'audit.read', 'integrations.manage'],
  },
  { to: '/admin/alerting', label: 'Alerting', anyOf: ['alerts.configure'] },
];

/**
 * Where each role lands after signing in — acceptance criterion C-09 asserts
 * ten sign-ins and ten correct landing routes. It is derived from the nav, so
 * a permission change moves the landing route with it.
 */
export function landingRoute(has: (permission: Permission) => boolean): string {
  const first = NAV_ITEMS.find((item) => item.anyOf.some(has));
  return first?.to ?? '/no-access';
}

export function visibleNavItems(has: (permission: Permission) => boolean): NavItem[] {
  return NAV_ITEMS.filter((item) => item.anyOf.some(has));
}
